import { useMemo } from 'react';

import { useGetOrderDocumentsQuery, useGetStopDocumentsQuery } from '#api/documentApi.slice.ts';
import type { RTKQueryOptions } from '#api/model/queryOptions.ts';

import { dedupeDocumentsById } from '../utils/dedupeDocumentsById.ts';
import { toOrderDetailsDocuments } from '../utils/toOrderDetailsDocuments.ts';

export const useGetOrderDetailsDocumentsQuery = (
    arg: {
        orderId: string;
        loadingStopId?: string;
        unloadingStopId?: string;
        executionStopId?: string;
    },
    options?: RTKQueryOptions
) => {
    const orderDocumentsQuery = useGetOrderDocumentsQuery({ orderId: arg.orderId }, options);
    const loadingStopDocumentsQuery = useGetStopDocumentsQuery(
        { stopId: arg.loadingStopId ?? '' },
        { ...options, skip: options?.skip === true || !arg.loadingStopId }
    );
    const unloadingStopDocumentsQuery = useGetStopDocumentsQuery(
        { stopId: arg.unloadingStopId ?? '' },
        { ...options, skip: options?.skip === true || !arg.unloadingStopId }
    );
    const executionStopDocumentsQuery = useGetStopDocumentsQuery(
        { stopId: arg.executionStopId ?? '' },
        { ...options, skip: options?.skip === true || !arg.executionStopId }
    );

    const data = useMemo(() => {
        if (options?.skip === true) {
            return undefined;
        }
        const documentSources = [
            orderDocumentsQuery.data?.items ?? [],
            loadingStopDocumentsQuery.data?.items ?? [],
            unloadingStopDocumentsQuery.data?.items ?? [],
            executionStopDocumentsQuery.data?.items ?? [],
        ];
        const totalDocumentsCount = documentSources.reduce((sum, documents) => sum + documents.length, 0);
        if (totalDocumentsCount === 0) {
            return undefined;
        }

        const nonEmptySources = documentSources.filter(documents => documents.length > 0);
        const mergedDocuments =
            nonEmptySources.length <= 1
                ? (nonEmptySources[0] ?? [])
                : dedupeDocumentsById([
                      ...documentSources[0],
                      ...documentSources[1],
                      ...documentSources[2],
                      ...documentSources[3],
                  ]);

        return toOrderDetailsDocuments(mergedDocuments, arg.loadingStopId, arg.unloadingStopId, arg.executionStopId);
    }, [
        arg,
        options,
        executionStopDocumentsQuery.data?.items,
        loadingStopDocumentsQuery.data?.items,
        orderDocumentsQuery.data?.items,
        unloadingStopDocumentsQuery.data?.items,
    ]);

    return {
        data,
    };
};
